import type { TreatmentSummaryProto } from "@noom/noom-contracts/noom_contracts/healthcare/model/treatment";
import type { ProductCatalogGetResponseProto } from "@noom/noom-contracts/noom_contracts/billing/product_catalog/product_catalog_service";
import type { SubscriptionProto } from "@noom/noom-contracts/noom_contracts/billing/subscription/v2/subscription";

export type CancellationMedication = {
  name: string;
  /** The card's secondary line, e.g. "Semaglutide · 4 mg". */
  details: string;
  imageSrc: string;
  /**
   * Whether the refill and refund bullets apply on the confirm cancellation screen.
   * When false (e.g. Zepbound KwikPen), a note pointing the user to their Care
   * Coordinator team is shown instead.
   */
  showRefillBullets: boolean;
};

// INTENDED covers members whose medication hasn't been activated (e.g. awaiting a
// prescription), who still have a medication attached to their membership.
const LIVE_TREATMENT_STATUSES: readonly TreatmentSummaryProto["status"][] = [
  "ACTIVE",
  "INTENDED",
];

function isLiveTreatment(treatment: TreatmentSummaryProto) {
  return LIVE_TREATMENT_STATUSES.includes(treatment.status);
}

/**
 * Returns the user's active or intended medication treatments, ACTIVE first. These
 * can include medications unrelated to the membership (e.g. sermorelin), so callers
 * still have to tie one to the membership or the cancelled subscription.
 */
export function getMedicationTreatmentCandidates(
  treatments: readonly TreatmentSummaryProto[]
): TreatmentSummaryProto[] {
  return treatments
    .filter((t) => t.treatmentType === "MED" && isLiveTreatment(t))
    .sort(
      (a, b) => Number(b.status === "ACTIVE") - Number(a.status === "ACTIVE")
    );
}

/**
 * Returns the candidate whose parent treatment is the user's active or intended
 * MEMBERSHIP treatment.
 */
export function findMembershipLinkedTreatment(
  allTreatments: readonly TreatmentSummaryProto[],
  candidates: readonly TreatmentSummaryProto[]
): TreatmentSummaryProto | undefined {
  const membershipTreatmentIds = new Set(
    allTreatments
      .filter((t) => t.treatmentType === "MEMBERSHIP" && isLiveTreatment(t))
      .map((t) => t.userTreatmentId)
  );
  return candidates.find(
    (t) =>
      !!t.parentUserTreatmentId &&
      membershipTreatmentIds.has(t.parentUserTreatmentId)
  );
}

/** Returns the candidate billed by the given subscription, matched on its sale item. */
export function findSubscriptionTreatment(
  candidates: readonly TreatmentSummaryProto[],
  subscription: SubscriptionProto | undefined
): TreatmentSummaryProto | undefined {
  const saleItemId = subscription?.saleItemId;
  if (!saleItemId) return undefined;
  return candidates.find((t) => t.currentSaleItemId === saleItemId);
}

function formatIngredient(ingredient: string) {
  const lower = ingredient.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** Builds the card's secondary line, e.g. "Semaglutide · 4 mg". */
export function formatMedicationDetails(
  activeIngredients: readonly string[] = [],
  dosageInMg?: number
) {
  const parts = [activeIngredients.map(formatIngredient).join(", ")];
  if (dosageInMg != null) parts.push(`${dosageInMg} mg`);
  return parts.filter(Boolean).join(" · ");
}

/**
 * Builds the card from the treatment and, when the lookup succeeded, its sale
 * item's catalog entry, which adds the dosage. Medications Noom doesn't sell are
 * paid at the pharmacy, with no medication subscription behind them, so they get
 * the Care Coordinator note instead of the refill bullets.
 */
export function buildCancellationMedication(
  treatment: TreatmentSummaryProto,
  catalogEntry?: ProductCatalogGetResponseProto
): CancellationMedication | undefined {
  const metadata = treatment.treatmentMetadata;
  const name = metadata?.displayName;
  const imageSrc = metadata?.medicationDisplay?.imageUrl ?? metadata?.iconUrl;
  if (!name || !imageSrc) return undefined;
  return {
    name,
    details: formatMedicationDetails(
      metadata.activeIngredients,
      catalogEntry?.subscriptionSaleItem?.dosageInMg
    ),
    imageSrc,
    // Unset only on payloads that predate the field, which the backend reads as sellable.
    showRefillBullets: treatment.isSellable ?? true,
  };
}
